import { Injectable } from "@angular/core";
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse
} from "@angular/common/http";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import * as Sentry from "@sentry/capacitor";
import { API_URL } from "../../environments/environment";

export const SLOW_REQUEST_THRESHOLD_MS = 10000;

// Path segments that may reach Sentry. Everything else (ids, emails, file
// names, search terms) is replaced, so no personal data ends up in a tag.
const KNOWN_SEGMENTS = new Set([
    "account",
    "anniversaries",
    "approve",
    "articles",
    "attend",
    "auth",
    "business-units",
    "capgo",
    "changes",
    "channels",
    "chapters",
    "check",
    "clear-pin",
    "colleagues",
    "contacts",
    "content-pages",
    "currency-types",
    "departments",
    "device-token",
    "documents",
    "email",
    "employee-functions",
    "employments",
    "events",
    "expense-types",
    "expenses",
    "favorite",
    "files",
    "form-submissions",
    "furlough",
    "ideabox",
    "image",
    "info",
    "languages",
    "link-device",
    "locations",
    "login",
    "logout",
    "manuals",
    "mark-read",
    "me",
    "accept-terms",
    "persons",
    "personal-documents",
    "poll",
    "privacy-policy",
    "push-notifications",
    "refresh-token",
    "remotestorage",
    "reports",
    "request-login",
    "reset-pin",
    "results",
    "salaries",
    "salary-attachments",
    "search",
    "settings",
    "show",
    "terms",
    "to-approve",
    "two-fa-pin-login",
    "unemployment",
    "unread-count",
    "user-divisions",
    "users",
    "v2",
    "verify-pin",
    "versions",
    "vote",
    "french-certificate"
]);

/**
 * Reduces a request url to a route template that is safe to send to Sentry.
 * Only known static segments are kept, all other segments become ":param".
 */
export function normalizeEndpoint(url: string): string {
    const path = url.replace(API_URL, "").split("?")[0];

    return path
        .split("/")
        .map((segment) =>
            !segment || KNOWN_SEGMENTS.has(segment) ? segment : ":param"
        )
        .join("/");
}

@Injectable()
export class SlowRequestInterceptor implements HttpInterceptor {
    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        const startedAt = performance.now();

        return next.handle(request).pipe(
            tap({
                next: (event) => {
                    if (event instanceof HttpResponse) {
                        this.report(request, event.status, startedAt);
                    }
                },
                error: (error) => {
                    const status =
                        error instanceof HttpErrorResponse ? error.status : 0;
                    this.report(request, status, startedAt);
                }
            })
        );
    }

    private report(request: HttpRequest<any>, status: number, startedAt) {
        const duration = Math.round(performance.now() - startedAt);
        const endpoint = normalizeEndpoint(request.url);

        Sentry.addBreadcrumb({
            category: "http.timing",
            message: `${request.method} ${endpoint}`,
            data: { duration_ms: duration, status },
            level: duration > SLOW_REQUEST_THRESHOLD_MS ? "warning" : "info"
        });

        if (duration <= SLOW_REQUEST_THRESHOLD_MS) {
            return;
        }

        Sentry.withScope((scope) => {
            scope.setTag("slow_request", "true");
            scope.setTag("endpoint", endpoint);
            scope.setTag("duration_bucket", this.bucket(duration));
            scope.setExtra("duration_ms", duration);
            scope.setExtra("status", status);
            scope.setFingerprint(["slow-request", request.method, endpoint]);
            Sentry.captureMessage(
                `Slow request: ${request.method} ${endpoint}`,
                "warning"
            );
        });
    }

    private bucket(duration: number): string {
        if (duration > 10000) {
            return ">10s";
        }
        if (duration > 5000) {
            return "5-10s";
        }
        if (duration > 3000) {
            return "3-5s";
        }
        return "1.5-3s";
    }
}
